import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import LockOutlinedIcon from 'components/material-symbol/icons/Lock'
import {
  SCHEDULE_TYPE_RECURRING,
  SCHEDULE_TYPE_SERIES,
} from './generate-occurrences'

const useStyles = makeStyles(theme => ({
  options: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  selected: {
    borderColor: '#0053FF',
    boxShadow: '0 0 0 1px #0053FF',
    backgroundColor: '#F5F8FF',
  },
  locked: {
    cursor: 'default',
    backgroundColor: theme.palette.grey[50],
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  description: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  coachPlusChip: {
    height: 20,
    fontSize: '0.6875rem',
    fontWeight: 600,
    backgroundColor: '#E0EAFF',
    color: '#001C57',
  },
  lockIcon: {
    marginLeft: 'auto',
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
  upgradeButton: {
    marginTop: theme.spacing(1),
  },
}))

const OPTIONS = [
  {
    value: SCHEDULE_TYPE_SERIES,
    title: 'Series',
    description:
      'One registration covers every session. Students sign up for the whole series.',
  },
  {
    value: SCHEDULE_TYPE_RECURRING,
    title: 'Recurring',
    description:
      'Repeats on the dates you choose. Each date has its own registration, so students drop in to any date.',
  },
]

const ScheduleTypeSelect = ({
  name = 'scheduleType',
  isRecurringLocked,
  onUpgradeClick,
  upgradeLoading,
  onTypeChange,
}) => {
  const classes = useStyles()
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={SCHEDULE_TYPE_SERIES}
      render={({ value, onChange }) => (
        <Box
          className={classes.options}
          role="radiogroup"
          aria-label="Event schedule type"
        >
          {OPTIONS.map(option => {
            const isRecurring = option.value === SCHEDULE_TYPE_RECURRING
            const isLocked = isRecurring && isRecurringLocked
            const isSelected = value === option.value

            return (
              <Box
                key={option.value}
                component={isLocked ? 'div' : 'button'}
                type={isLocked ? undefined : 'button'}
                role="radio"
                aria-checked={isSelected}
                aria-disabled={isLocked || undefined}
                data-testid={`schedule-type-${option.value}`}
                className={clsx(
                  classes.option,
                  isSelected && classes.selected,
                  isLocked && classes.locked,
                )}
                onClick={
                  isLocked || isSelected
                    ? undefined
                    : () => {
                      onTypeChange?.(option.value)
                      onChange(option.value)
                    }
                }
              >
                <Box className={classes.titleRow}>
                  <Typography component="span" className={classes.title}>
                    {option.title}
                  </Typography>
                  {isRecurring ? (
                    <Chip
                      size="small"
                      label="Coach+"
                      className={classes.coachPlusChip}
                    />
                  ) : null}
                  {isLocked ? (
                    <LockOutlinedIcon className={classes.lockIcon} />
                  ) : null}
                </Box>
                <Typography component="span" className={classes.description}>
                  {option.description}
                </Typography>
                {isLocked ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    className={classes.upgradeButton}
                    onClick={onUpgradeClick}
                    disabled={upgradeLoading}
                  >
                    Upgrade to unlock
                  </Button>
                ) : null}
              </Box>
            )
          })}
        </Box>
      )}
    />
  )
}

ScheduleTypeSelect.propTypes = {
  name: PropTypes.string,
  isRecurringLocked: PropTypes.bool,
  onUpgradeClick: PropTypes.func,
  upgradeLoading: PropTypes.bool,
  onTypeChange: PropTypes.func,
}

export default ScheduleTypeSelect
